import { useCallback, useEffect, useState } from 'react';

import { useLoginState } from '@xing-com/hub';

// V3 Usercentrics API types
type TCFVendor = {
  id: number;
  name: string;
  consent: boolean | null;
};

type V3CmpController = {
  tcf: {
    vendors: Record<number, TCFVendor>;
  };
};

// V2 Usercentrics API types
type V2TCFVendor = {
  id: number;
  name: string;
  consent: boolean;
};

type V2UCInterface = {
  getTCFVendors: () => V2TCFVendor[];
};

declare global {
  interface Window {
    UC_UI?: V2UCInterface;
    __ucCmp?: {
      cmpController?: V3CmpController;
    };
  }
}

// https://usercentrics.com/docs/web/features/events/uc-ui-cmp-event/
type EventDetail = {
  source: 'none' | 'button' | 'first' | 'second' | 'embeddings' | '__ucCmp';
  type:
    | 'CMP_SHOWN'
    | 'ACCEPT_ALL'
    | 'DENY_ALL'
    | 'SAVE'
    | 'MORE_INFORMATION_LINK'
    | 'IMPRINT_LINK'
    | 'PRIVACY_POLICY_LINK';
};

const YIELDLOVE_VENDOR_ID = 251; // YieldLove GmbH

// Runtime detection: get marketing consent using V3 or V2 API
const getMarketingConsent = async (): Promise<boolean> => {
  try {
    // V3 API (async) - check first as UC_UI is also present in V3
    if (window.__ucCmp?.cmpController) {
      const tcfVendors = window.__ucCmp.cmpController.tcf.vendors;
      return tcfVendors[YIELDLOVE_VENDOR_ID]?.consent === true;
    }
    // V2 API (sync) - fallback
    if (window.UC_UI?.getTCFVendors) {
      const tcfVendors = window.UC_UI.getTCFVendors();
      const yieldLoveVendor = tcfVendors.find(
        (vendor) => vendor.id === YIELDLOVE_VENDOR_ID
      );
      return yieldLoveVendor?.consent === true;
    }
    return false;
  } catch {
    return false;
  }
};

export const useDisplayAds = (): { displayAdsAllowed: boolean } => {
  const isLoggedOut = useLoginState() === 'LOGGED_OUT';
  const [hasMarketingConsent, setHasMarketingConsent] = useState(false);

  const isDebugMode =
    global?.window?.localStorage?.getItem('xam_display_ads_debug') === 'true';

  const displayAdsAllowed =
    (isLoggedOut && isDebugMode) || (isLoggedOut && hasMarketingConsent);

  const updateMarketingConsent = useCallback((): void => {
    getMarketingConsent().then((value) => setHasMarketingConsent(value));
  }, []);

  const handleCmpEvent = useCallback(
    (event: Event): void => {
      const customEvent = event as CustomEvent<EventDetail>;
      const eventType = customEvent.detail?.type;
      if (
        eventType === 'SAVE' ||
        eventType === 'ACCEPT_ALL' ||
        eventType === 'DENY_ALL'
      ) {
        updateMarketingConsent();
      }
    },
    [updateMarketingConsent]
  );

  useEffect(() => {
    window.addEventListener('UC_UI_INITIALIZED', updateMarketingConsent);
    window.addEventListener('UC_UI_CMP_EVENT', handleCmpEvent);

    return () => {
      window.removeEventListener('UC_UI_INITIALIZED', updateMarketingConsent);
      window.removeEventListener('UC_UI_CMP_EVENT', handleCmpEvent);
    };
  }, [updateMarketingConsent, handleCmpEvent]);

  return {
    displayAdsAllowed,
  };
};
